import type { ExtractedPdf, TextItem } from './types'

// Lazily load pdfjs-dist so it stays out of the initial marketing-page bundle.
// Only pulled in when the user actually uploads a file.
let workerConfigured = false

async function loadPdfjs() {
  const pdfjs = await import('pdfjs-dist')
  if (!workerConfigured) {
    // Bundler-resolved worker URL (works under Next + Turbopack/Webpack).
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url,
    ).toString()
    workerConfigured = true
  }
  return pdfjs
}

export async function extractPdfText(
  source: File | ArrayBuffer,
): Promise<ExtractedPdf> {
  const pdfjs = await loadPdfjs()
  const data =
    source instanceof ArrayBuffer ? source : await source.arrayBuffer()

  const doc = await pdfjs.getDocument({ data }).promise

  const items: TextItem[] = []
  let totalChars = 0
  let pageWidth = 0

  for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber += 1) {
    const page = await doc.getPage(pageNumber)
    const viewport = page.getViewport({ scale: 1 })
    pageWidth = Math.max(pageWidth, viewport.width)

    const content = await page.getTextContent()
    const styles = content.styles as Record<
      string,
      { fontFamily?: string } | undefined
    >
    for (const item of content.items) {
      // Skip marked-content markers; keep only real text runs.
      if (!('str' in item)) continue
      const str = item.str
      if (!str) continue

      const transform = item.transform as number[]
      const fontKey = item.fontName
      const fontFamily = styles[fontKey]?.fontFamily ?? fontKey
      const bold = /bold|black|heavy|semibold|[-_]bd\b/i.test(fontFamily)

      items.push({
        str,
        x: transform[4] ?? 0,
        y: transform[5] ?? 0,
        height: item.height,
        width: item.width,
        page: pageNumber,
        fontName: fontFamily,
        bold,
      })
      totalChars += str.trim().length
    }
  }

  return { items, totalChars, pageCount: doc.numPages, pageWidth }
}
